import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useState } from "react";
import { GripVertical, Loader2, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";

import { trpc } from "~/api/trpc";

type WaitingRoom = {
    id: string;
    name: string;
    sequence: number;
    createdAt: Date;
    storeId: number;
};

type WaitingRoomsTabProps = {
    storeId: number;
};

const SortableRow = ({
    room,
    onEdit,
    onDelete,
}: {
    room: WaitingRoom;
    onEdit: (room: WaitingRoom) => void;
    onDelete: (room: WaitingRoom) => void;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: room.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <TableRow ref={setNodeRef} style={style}>
            <TableCell className="w-[40px]">
                <button
                    type="button"
                    className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
                    {...attributes}
                    {...listeners}
                >
                    <GripVertical className="h-4 w-4" />
                </button>
            </TableCell>
            <TableCell className="font-medium">{room.name}</TableCell>
            <TableCell>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(room)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Bearbeiten
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => onDelete(room)}
                        >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Löschen
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </TableCell>
        </TableRow>
    );
};

const WaitingRoomsTab = ({ storeId }: WaitingRoomsTabProps) => {
    const utils = trpc.useUtils();

    const { data: waitingRooms, isLoading } = trpc.waitingRooms.getByStoreId.useQuery({ storeId });

    const [orderedRooms, setOrderedRooms] = useState<WaitingRoom[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRoom, setEditingRoom] = useState<WaitingRoom | null>(null);
    const [name, setName] = useState("");

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roomToDelete, setRoomToDelete] = useState<WaitingRoom | null>(null);

    useEffect(() => {
        if (waitingRooms) {
            setOrderedRooms(waitingRooms as WaitingRoom[]);
        }
    }, [waitingRooms]);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const invalidate = () => {
        utils.waitingRooms.getByStoreId.invalidate({ storeId });
        utils.waitingRooms.getAll.invalidate();
    };

    const createMutation = trpc.waitingRooms.create.useMutation({
        onSuccess: () => {
            invalidate();
            toast.success("Warteraum erstellt");
            closeDialog();
        },
        onError: (error) => {
            toast.error("Fehler beim Erstellen", { description: error.message });
        },
    });

    const updateMutation = trpc.waitingRooms.update.useMutation({
        onSuccess: () => {
            invalidate();
            toast.success("Warteraum aktualisiert");
            closeDialog();
        },
        onError: (error) => {
            toast.error("Fehler beim Aktualisieren", { description: error.message });
        },
    });

    const deleteMutation = trpc.waitingRooms.delete.useMutation({
        onSuccess: () => {
            invalidate();
            toast.success("Warteraum gelöscht");
            setDeleteDialogOpen(false);
            setRoomToDelete(null);
        },
        onError: (error) => {
            toast.error("Fehler beim Löschen", { description: error.message });
        },
    });

    const reorderMutation = trpc.waitingRooms.reorder.useMutation({
        onError: (error) => {
            toast.error("Fehler beim Sortieren", { description: error.message });
        },
    });

    const openCreateDialog = () => {
        setEditingRoom(null);
        setName("");
        setDialogOpen(true);
    };

    const openEditDialog = (room: WaitingRoom) => {
        setEditingRoom(room);
        setName(room.name);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingRoom(null);
        setName("");
    };

    const handleSave = () => {
        const trimmed = name.trim();
        if (!trimmed) return;

        if (editingRoom) {
            updateMutation.mutate({ id: editingRoom.id, name: trimmed });
        } else {
            createMutation.mutate({ name: trimmed, storeId });
        }
    };

    const handleDeleteConfirm = () => {
        if (roomToDelete) {
            deleteMutation.mutate({ id: roomToDelete.id });
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = orderedRooms.findIndex((r) => r.id === active.id);
        const newIndex = orderedRooms.findIndex((r) => r.id === over.id);
        const reordered = arrayMove(orderedRooms, oldIndex, newIndex);
        setOrderedRooms(reordered);

        reorderMutation.mutate({
            orders: reordered.map((r, idx) => ({ id: r.id, sequence: idx + 1 })),
        });
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-4 w-72 mt-1" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-32 w-full" />
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <div>
                        <CardTitle>Warteräume</CardTitle>
                        <CardDescription className="mt-1">
                            Wartebereiche in der Empfangs-Spalte des Live-Boards. Reihenfolge per Drag & Drop ändern.
                        </CardDescription>
                    </div>
                    <Button size="sm" onClick={openCreateDialog}>
                        <Plus className="mr-2 h-4 w-4" />
                        Hinzufügen
                    </Button>
                </CardHeader>
                <CardContent>
                    {orderedRooms.length > 0 ? (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={orderedRooms.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[40px]" />
                                            <TableHead>Name</TableHead>
                                            <TableHead className="w-[60px]" />
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {orderedRooms.map((room) => (
                                            <SortableRow
                                                key={room.id}
                                                room={room}
                                                onEdit={openEditDialog}
                                                onDelete={(r) => {
                                                    setRoomToDelete(r);
                                                    setDeleteDialogOpen(true);
                                                }}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </SortableContext>
                        </DndContext>
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">
                            Noch keine Warteräume vorhanden.
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create / Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editingRoom ? "Warteraum bearbeiten" : "Neuer Warteraum"}
                        </DialogTitle>
                    </DialogHeader>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSave();
                        }}
                    >
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="waitingRoomName">Name</Label>
                                <Input
                                    id="waitingRoomName"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="z.B. Wartebereich Erdgeschoss"
                                    autoFocus
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={closeDialog}>
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={!name.trim() || isSaving}>
                                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {editingRoom ? "Speichern" : "Erstellen"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Warteraum löschen</AlertDialogTitle>
                        <AlertDialogDescription>
                            Soll der Warteraum &ldquo;{roomToDelete?.name}&rdquo; wirklich gelöscht werden?
                            Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default WaitingRoomsTab;
